import type { PlatformBridge, PlatformType, PlatformUser, ShareParams, ThemeColors } from './types.ts';

/**
 * Минимальные типы Telegram WebApp SDK.
 * Полные типы: https://core.telegram.org/bots/webapps
 */
interface TelegramWebApp {
  ready(): void;
  close(): void;
  initData: string;
  initDataUnsafe: {
    user?: {
      id: number;
      first_name?: string;
      last_name?: string;
      username?: string;
    };
  };
  requestContact(callback: (shared: boolean, contact?: { phone_number: string }) => void): void;
  BackButton: {
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
  };
  HapticFeedback: {
    impactOccurred(style: 'light' | 'medium' | 'heavy'): void;
  };
  themeParams: {
    bg_color?: string;
    text_color?: string;
    hint_color?: string;
    link_color?: string;
    button_color?: string;
    button_text_color?: string;
    secondary_bg_color?: string;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp };
  }
}

const DEFAULT_THEME: ThemeColors = {
  bgColor: '#ffffff',
  textColor: '#1a1a1a',
  hintColor: '#888888',
  linkColor: '#2563eb',
  buttonColor: '#16a34a',
  buttonTextColor: '#ffffff',
  secondaryBgColor: '#f3f4f6',
};

export class TelegramBridge implements PlatformBridge {
  private tg: TelegramWebApp;

  constructor() {
    if (!window.Telegram?.WebApp) {
      throw new Error('Telegram WebApp SDK not available');
    }
    this.tg = window.Telegram.WebApp;
  }

  getPlatform(): PlatformType {
    return 'telegram';
  }

  getUser(): PlatformUser {
    const u = this.tg.initDataUnsafe.user;
    return {
      id: u?.id ?? 0,
      firstName: u?.first_name ?? '',
      lastName: u?.last_name ?? '',
      username: u?.username ?? null,
      phone: null,
    };
  }

  getInitData(): string {
    return this.tg.initData;
  }

  requestPhone(): Promise<string | null> {
    return new Promise((resolve) => {
      this.tg.requestContact((shared, contact) => {
        resolve(shared && contact ? contact.phone_number : null);
      });
    });
  }

  showBackButton(): void {
    this.tg.BackButton.show();
  }

  hideBackButton(): void {
    this.tg.BackButton.hide();
  }

  onBackButtonClick(cb: () => void): void {
    this.tg.BackButton.onClick(cb);
  }

  impactOccurred(style: 'light' | 'medium' | 'heavy'): void {
    this.tg.HapticFeedback.impactOccurred(style);
  }

  getTheme(): ThemeColors {
    const t = this.tg.themeParams;
    return {
      bgColor: t.bg_color ?? DEFAULT_THEME.bgColor,
      textColor: t.text_color ?? DEFAULT_THEME.textColor,
      hintColor: t.hint_color ?? DEFAULT_THEME.hintColor,
      linkColor: t.link_color ?? DEFAULT_THEME.linkColor,
      buttonColor: t.button_color ?? DEFAULT_THEME.buttonColor,
      buttonTextColor: t.button_text_color ?? DEFAULT_THEME.buttonTextColor,
      secondaryBgColor: t.secondary_bg_color ?? DEFAULT_THEME.secondaryBgColor,
    };
  }

  ready(): void {
    this.tg.ready();
  }

  close(): void {
    this.tg.close();
  }

  openLink(url: string): void {
    window.open(url, '_blank');
  }

  openMaxLink(url: string): void {
    window.open(url, '_blank');
  }

  async share(_params: ShareParams): Promise<'shared' | 'cancelled'> {
    return 'cancelled';
  }

  enableClosingConfirmation(): void {
    // Telegram: window.Telegram?.WebApp.enableClosingConfirmation?.()
  }

  disableClosingConfirmation(): void {
    // Telegram: window.Telegram?.WebApp.disableClosingConfirmation?.()
  }
}
